import cx from 'classnames';
import { useState } from 'react';

import { Spinner } from '@components/Spinner';
import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { todayInTimezone } from '@shared/utils/dateFormatters';
import typography from '~styles/typography.scss';
import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useRestaurantEventsQuery } from './hooks';
import styles from './RestaurantEventsPage.scss';
import { partitionByActiveInactiveGroupedByDate } from './utils';
import { AddCard } from '@components/addCard/AddCard';
import { ReserveEventButton } from './booking/ReserveEventButton';
import { RestaurantEventCard } from './RestaurantEventCard';
import { RestaurantEventManagementModal } from './RestaurantEventManagementModal';

const LoadingState = () => {
  return (
    <>
      <PageHeader category="Operations" title="Events" />

      <PageContent>
        <div className={styles.mainContent}>
          <Spinner />
        </div>
      </PageContent>
    </>
  );
};

enum Tab {
  ACTIVE = 'active',
  EXPIRED = 'expired',
}

export const RestaurantEventsPage = () => {
  const [isOpen, setIsOpen] = useState(false);

  const restaurant = useRestaurant();
  const { data: restaurantEvents, isLoading } = useRestaurantEventsQuery();
  const [tab, setTab] = useState(Tab.ACTIVE);

  const today = todayInTimezone(restaurant.timezone);
  const aggregated = partitionByActiveInactiveGroupedByDate(
    restaurantEvents,
    today,
    true,
  );
  const eventsForTabGroupedByDate = aggregated[tab];
  const [selectedRestaurantEventId, setSelectedRestaurantEventId] = useState<
    string | null
  >(null);

  if (isLoading) return <LoadingState />;

  const handleEdit = (restaurantEventId: string) => {
    setIsOpen(true);
    setSelectedRestaurantEventId(restaurantEventId);
  };

  const handleCreateEvent = () => {
    setIsOpen(true);
    setSelectedRestaurantEventId(null);
  };

  return (
    <>
      <PageHeader category="Operations" title="Events">
        <Button
          label="Create Event"
          variant={ButtonVariants.Secondary}
          onClick={handleCreateEvent}
        />
      </PageHeader>

      <PageContent>
        <div className={styles.mainContent}>
          <div role="tablist" className={styles.tabBar}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === Tab.ACTIVE}
              onClick={() => setTab(Tab.ACTIVE)}
              className={cx(styles.tab, {
                [styles.activeTab]: tab === Tab.ACTIVE,
              })}
            >
              Active
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={tab === Tab.EXPIRED}
              onClick={() => setTab(Tab.EXPIRED)}
              className={cx(styles.tab, {
                [styles.activeTab]: tab === Tab.EXPIRED,
              })}
            >
              Expired
            </button>
          </div>
          <RestaurantEventManagementModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            selectedRestaurantEventId={selectedRestaurantEventId}
          />

          {restaurantEvents.length ? (
            <div
              role="tabpanel"
              className={cx(styles.tabPanel, {
                [styles.inactive]: tab === Tab.EXPIRED,
              })}
            >
              <div className={styles.restaurantEventsList}>
                {Object.keys(eventsForTabGroupedByDate)
                  .sort()
                  .map((date) => {
                    return eventsForTabGroupedByDate[date].map(
                      (restaurantEvent) => (
                        <RestaurantEventCard
                          restaurantEvent={restaurantEvent}
                          key={restaurantEvent.id}
                          onEdit={() => handleEdit(restaurantEvent?.id)}
                          action={
                            tab === Tab.ACTIVE && (
                              <ReserveEventButton
                                eventId={restaurantEvent.id}
                              />
                            )
                          }
                        />
                      ),
                    );
                  })}
                <div className={styles.addEventCardWrapper}>
                  <AddCard
                    className={styles.addEventCard}
                    containerClassName={styles.addEventCardContainer}
                    label="Create Event"
                    onClick={handleCreateEvent}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.noEventsContainer}>
              <div className={styles.noEventsIconBackground}>
                <Icon name="eventCalendar" className={styles.noEventsIcon} />
              </div>

              <div
                className={cx(typography.h4, styles.noEventsHeader)}
                onClick={() => setIsOpen(true)}
              >
                Click Create Event to Get Started
              </div>
            </div>
          )}
        </div>
      </PageContent>
    </>
  );
};
